/**
 * Projects section for /settings/github (admin) — enterprise-project-
 * organization Phase B1.
 *
 * A project is a curated repo set (pins ∪ tag rule) living in exactly one
 * space; who can see it is the space's members, so access is read-only here
 * with a pointer to the Spaces/Members admin. The editor previews the
 * would-be membership (server dry-run) before saving; membership itself is
 * materialized asynchronously after save.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, FolderKanban, Plus, Users } from 'lucide-react';
import { Spinner, LoadingState } from '@systemdox/ui';
import { Link } from 'react-router-dom';
import {
  projectsApi,
  type Project,
  type ProjectMember,
  type ProjectRule,
  type ProjectRuleClause,
  type TaxonomyDimension,
} from '@/services/projectsApi';
import type { Repo } from '@/services/githubApi';
import type { Space } from '@/services/spacesApi';
import { valueLabel } from '@/components/settings/TagChips';
import { cn } from '@/lib/utils';

type ClauseKind = 'all' | 'any' | 'none';
const KIND_LABEL: Record<ClauseKind, string> = {
  all: 'must match',
  any: 'match at least one of',
  none: 'exclude',
};

interface EditorState {
  projectId: string | null; // null = creating
  name: string;
  description: string;
  spaceId: string;
  clauses: { kind: ClauseKind; dim: string; values: string[] }[];
  pins: string[];
}

function toRule(clauses: EditorState['clauses']): ProjectRule | null {
  const rule: ProjectRule = {};
  for (const kind of ['all', 'any', 'none'] as ClauseKind[]) {
    const list: ProjectRuleClause[] = clauses
      .filter((c) => c.kind === kind && c.dim && c.values.length > 0)
      .map((c) => ({ dim: c.dim, in: c.values }));
    if (list.length) rule[kind] = list;
  }
  return rule.all || rule.any ? rule : null;
}

function fromRule(rule: ProjectRule | null): EditorState['clauses'] {
  if (!rule) return [];
  const out: EditorState['clauses'] = [];
  for (const kind of ['all', 'any', 'none'] as ClauseKind[]) {
    for (const clause of rule[kind] ?? []) {
      out.push({ kind, dim: clause.dim, values: clause.in });
    }
  }
  return out;
}

export function ProjectsSection({
  dimensions,
  spaces,
  repos,
}: {
  dimensions: TaxonomyDimension[];
  spaces: Space[];
  repos: Repo[];
}) {
  const [open, setOpen] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<{ total: number; repos: ProjectMember[] } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [pinSearch, setPinSearch] = useState('');

  const spaceName = useCallback(
    (spaceId: string) => spaces.find((s) => s.spaceId === spaceId)?.name ?? spaceId,
    [spaces],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // 'manage', not the default 'read': this screen administers projects, so
      // it must also see non-sealed spaces an admin holds no content grant on.
      // The header selector deliberately does NOT — picking a project there
      // filters every surface to its repos.
      const { projects: list } = await projectsApi.listProjects(undefined, 'manage');
      const detailed = await Promise.all(list.map((p) => projectsApi.getProject(p.projectId)));
      setProjects(detailed);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const activeDims = dimensions.filter((d) => !d.archivedAt);

  function startCreate() {
    setPreview(null);
    setEditor({
      projectId: null,
      name: '',
      description: '',
      spaceId: spaces.find((s) => s.isDefault)?.spaceId ?? spaces[0]?.spaceId ?? 'spc_default',
      clauses: [],
      pins: [],
    });
  }

  function startEdit(project: Project) {
    setPreview(null);
    setEditor({
      projectId: project.projectId,
      name: project.name,
      description: project.description,
      spaceId: project.spaceId,
      clauses: fromRule(project.rule),
      pins: project.pinnedRepos,
    });
  }

  async function runPreview(state: EditorState) {
    setPreviewing(true);
    try {
      setPreview(await projectsApi.previewMembership({ rule: toRule(state.clauses), pinnedRepos: state.pins }));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  }

  async function save() {
    if (!editor) return;
    setSaving(true);
    setError(null);
    const input = {
      name: editor.name.trim(),
      description: editor.description.trim(),
      spaceId: editor.spaceId,
      rule: toRule(editor.clauses),
      pinnedRepos: editor.pins,
    };
    try {
      if (editor.projectId) await projectsApi.updateProject(editor.projectId, input);
      else await projectsApi.createProject(input);
      setEditor(null);
      setPreview(null);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save project');
    } finally {
      setSaving(false);
    }
  }

  async function archive(project: Project) {
    setError(null);
    try {
      await projectsApi.updateProject(project.projectId, { archived: !project.archivedAt });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not archive project');
    }
  }

  const pinMatches = useMemo(() => {
    if (!pinSearch) return [];
    const q = pinSearch.toLowerCase();
    return repos
      .filter((r) => r.full_name.toLowerCase().includes(q))
      .filter((r) => !editor?.pins.includes(r.full_name))
      .slice(0, 8);
  }, [pinSearch, repos, editor?.pins]);

  return (
    <section className="mt-10">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 text-lg font-semibold text-slate-900 dark:text-slate-100"
        >
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <FolderKanban className="w-4 h-4 text-slate-500" />
          Projects
        </button>
        {open && !editor && (
          <button
            type="button"
            onClick={startCreate}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700"
          >
            <Plus className="w-3.5 h-3.5" /> New project
          </button>
        )}
      </div>
      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 mb-4">
        Curated repository sets — pinned repos plus a tag rule — that users select in the header.
        A project lives in one space; the space's members are who can see it.
      </p>
      {!open ? null : (
        <>
          {error && (
            <p className="mb-3 p-2 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-xs">
              {error}
            </p>
          )}

          {editor && (
            <div className="mb-6 rounded-lg border border-blue-200 dark:border-blue-900 p-4 space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-xs text-slate-600 dark:text-slate-400">
                  Name
                  <input
                    value={editor.name}
                    onChange={(e) => setEditor({ ...editor, name: e.target.value })}
                    placeholder="Checkout"
                    className="mt-0.5 w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                  />
                </label>
                <label className="text-xs text-slate-600 dark:text-slate-400">
                  Space (controls who can see this project)
                  <select
                    value={editor.spaceId}
                    onChange={(e) => setEditor({ ...editor, spaceId: e.target.value })}
                    className="mt-0.5 w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                  >
                    {spaces
                      .filter((s) => !s.archivedAt)
                      .map((s) => (
                        <option key={s.spaceId} value={s.spaceId}>
                          {s.name}
                          {s.visibility === 'restricted' ? ' (restricted)' : ''}
                        </option>
                      ))}
                  </select>
                </label>
              </div>
              <label className="block text-xs text-slate-600 dark:text-slate-400">
                Description
                <input
                  value={editor.description}
                  onChange={(e) => setEditor({ ...editor, description: e.target.value })}
                  className="mt-0.5 w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                />
              </label>

              {/* Tag rule builder */}
              <div>
                <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
                  Tag rule — repos matching ALL "must match" rows, at least one "match at least
                  one of" row (when present), and none of the exclusions
                </p>
                <div className="space-y-1.5">
                  {editor.clauses.map((clause, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-1.5 text-sm">
                      <select
                        value={clause.kind}
                        onChange={(e) => {
                          const clauses = [...editor.clauses];
                          clauses[i] = { ...clause, kind: e.target.value as ClauseKind };
                          setEditor({ ...editor, clauses });
                        }}
                        className="rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-1.5 py-1 text-xs"
                      >
                        {(Object.keys(KIND_LABEL) as ClauseKind[]).map((k) => (
                          <option key={k} value={k}>
                            {KIND_LABEL[k]}
                          </option>
                        ))}
                      </select>
                      <select
                        value={clause.dim}
                        onChange={(e) => {
                          const clauses = [...editor.clauses];
                          clauses[i] = { ...clause, dim: e.target.value, values: [] };
                          setEditor({ ...editor, clauses });
                        }}
                        className="rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-1.5 py-1 text-xs"
                      >
                        <option value="">dimension…</option>
                        {activeDims.map((d) => (
                          <option key={d.dimKey} value={d.dimKey}>
                            {d.label}
                          </option>
                        ))}
                      </select>
                      <span className="flex flex-wrap items-center gap-1">
                        {clause.values.map((v) => (
                          <button
                            key={v}
                            type="button"
                            title="Remove"
                            onClick={() => {
                              const clauses = [...editor.clauses];
                              clauses[i] = { ...clause, values: clause.values.filter((x) => x !== v) };
                              setEditor({ ...editor, clauses });
                            }}
                            className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-xs hover:bg-slate-200"
                          >
                            {valueLabel(dimensions, clause.dim, v)} ×
                          </button>
                        ))}
                        {clause.dim && (
                          <select
                            value=""
                            onChange={(e) => {
                              if (!e.target.value) return;
                              const clauses = [...editor.clauses];
                              clauses[i] = { ...clause, values: [...clause.values, e.target.value] };
                              setEditor({ ...editor, clauses });
                            }}
                            className="rounded border border-dashed border-slate-300 dark:border-slate-600 bg-transparent px-1 py-0.5 text-xs text-slate-400"
                          >
                            <option value="">+ value…</option>
                            {activeDims
                              .find((d) => d.dimKey === clause.dim)
                              ?.values.filter((v) => !v.archivedAt && !clause.values.includes(v.valueSlug))
                              .map((v) => (
                                <option key={v.valueSlug} value={v.valueSlug}>
                                  {v.label}
                                </option>
                              ))}
                          </select>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setEditor({ ...editor, clauses: editor.clauses.filter((_, j) => j !== i) })
                        }
                        className="ml-auto text-xs text-slate-400 hover:text-red-500"
                      >
                        remove
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setEditor({
                      ...editor,
                      clauses: [...editor.clauses, { kind: 'all', dim: '', values: [] }],
                    })
                  }
                  className="mt-1.5 inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <Plus className="w-3 h-3" /> Add rule row
                </button>
              </div>

              {/* Pinned repos */}
              <div>
                <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
                  Pinned repositories (always members, any GitHub org)
                </p>
                <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                  {editor.pins.map((pin) => (
                    <button
                      key={pin}
                      type="button"
                      title="Unpin"
                      onClick={() => setEditor({ ...editor, pins: editor.pins.filter((p) => p !== pin) })}
                      className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-xs hover:bg-slate-200"
                    >
                      {pin} ×
                    </button>
                  ))}
                </div>
                <input
                  value={pinSearch}
                  onChange={(e) => setPinSearch(e.target.value)}
                  placeholder="Search repositories to pin…"
                  className="w-72 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                />
                {pinMatches.length > 0 && (
                  <div className="mt-1 w-72 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800">
                    {pinMatches.map((r) => (
                      <button
                        key={r.full_name}
                        type="button"
                        onClick={() => {
                          setEditor({ ...editor, pins: [...editor.pins, r.full_name].sort() });
                          setPinSearch('');
                        }}
                        className="block w-full px-2 py-1 text-left text-xs hover:bg-slate-50 dark:hover:bg-slate-800"
                      >
                        {r.full_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Preview + actions */}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => runPreview(editor)}
                  disabled={previewing}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  {previewing && <Spinner size="sm" tone="current" />}
                  Preview membership
                </button>
                <button
                  type="button"
                  onClick={save}
                  disabled={saving || !editor.name.trim()}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving && <Spinner size="sm" tone="current" />}
                  {editor.projectId ? 'Save changes' : 'Create project'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditor(null);
                    setPreview(null);
                  }}
                  className="px-3 py-1 rounded text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  Cancel
                </button>
              </div>
              {preview && (
                <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2">
                  <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                    {preview.total} repositor{preview.total === 1 ? 'y' : 'ies'} would be members
                    {preview.total > preview.repos.length && ` (showing first ${preview.repos.length})`}
                  </p>
                  <div className="max-h-40 overflow-y-auto text-xs text-slate-600 dark:text-slate-300 space-y-0.5">
                    {preview.repos.map((r) => (
                      <div key={r.repoFullName} className="flex items-center justify-between">
                        <span>{r.repoFullName}</span>
                        <span className="text-slate-400">{r.via}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {loading ? (
            <LoadingState label="Loading projects…" />
          ) : projects.length === 0 && !editor ? (
            <p className="text-sm text-slate-500 dark:text-slate-400 py-4 text-center border border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
              No projects yet. Tag repositories above, then compose your first project — it will
              appear in every user's header selector.
            </p>
          ) : (
            <div className="space-y-1">
              {projects.map((project) => (
                <div
                  key={project.projectId}
                  className={cn(
                    'flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900',
                    project.archivedAt && 'opacity-60',
                  )}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                        {project.name}
                      </span>
                      <span className="text-xs text-slate-400">/p/{project.slug}</span>
                      {project.archivedAt && (
                        <span className="text-[11px] rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-slate-500">
                          archived
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-2">
                      <span>{project.repoCount} repos</span>
                      <span className="inline-flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {spaceName(project.spaceId)} —{' '}
                        <Link to="/settings/spaces" className="underline hover:text-slate-700">
                          manage access
                        </Link>
                      </span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    <button
                      type="button"
                      onClick={() => startEdit(project)}
                      className="px-3 py-1 rounded text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => archive(project)}
                      className="px-3 py-1 rounded text-xs font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      {project.archivedAt ? 'Restore' : 'Archive'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
